import { useEffect, useState } from 'react';
import SearchableSelect from '../components/SearchableSelect';
import { getFilterOptions } from '../api/dashboard';
import { getPriceHistory } from '../api/sales';
import { formatCurrency } from '../utils/format';

export default function PriceChecker() {
  const [options, setOptions] = useState({ products: [], customers: [] });
  const [productCode, setProductCode] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [data, setData] = useState({ items: [], total: 0, truncated: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFilterOptions().then(setOptions);
  }, []);

  useEffect(() => {
    if (!productCode) {
      setData({ items: [], total: 0, truncated: false });
      return;
    }
    setLoading(true);
    getPriceHistory({ productCode, ...(customerCode ? { customerCode } : {}) }).then((res) => {
      setData(res);
      setLoading(false);
    });
  }, [productCode, customerCode]);

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-800 mb-4">Price Checker</h1>
      <p className="text-sm text-gray-600 mb-4">
        Search a Product by Name or Part Number to see its recent sale history — most recent invoice
        first. Optionally narrow it down to one Customer.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
        <SearchableSelect label="Product" value={productCode} onChange={setProductCode} items={options.products} />
        <SearchableSelect label="Customer" value={customerCode} onChange={setCustomerCode} items={options.customers} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-medium text-gray-700">
            {productCode ? `Sale history — latest first` : 'Select a Product above to see its sale history'}
          </h2>
          {productCode && <span className="text-xs text-gray-500">{data.total} rows</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-2 font-medium">Customer</th>
              <th className="px-4 py-2 font-medium">Salesman</th>
              <th className="px-4 py-2 font-medium">Invoice Number</th>
              <th className="px-4 py-2 font-medium">Invoice Date</th>
              <th className="px-4 py-2 font-medium text-right">Qty</th>
              <th className="px-4 py-2 font-medium">Unit</th>
              <th className="px-4 py-2 font-medium text-right">Selling Price</th>
              <th className="px-4 py-2 font-medium text-right">Discount</th>
              <th className="px-4 py-2 font-medium text-right">Net Unit Price</th>
            </tr>
          </thead>
          <tbody>
            {!productCode ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  No product selected
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                  No sale history found for this selection
                </td>
              </tr>
            ) : (
              data.items.map((r, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-4 py-2">{r.customerName}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.salesmanName}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.invoiceNumber}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{new Date(r.date).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">{r.qty}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.unit}</td>
                  <td className="px-4 py-2 text-right">{r.unitPrice ? formatCurrency(r.unitPrice) : '—'}</td>
                  <td className="px-4 py-2 text-right">{r.discountPercent ? `${r.discountPercent.toFixed(1)}%` : '—'}</td>
                  <td className="px-4 py-2 text-right font-medium">{formatCurrency(r.netUnitPrice)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {data.truncated && (
          <p className="text-xs text-gray-400 px-4 py-2 border-t border-gray-100">
            Showing the most recent 300 rows — narrow down by Customer to see more specific history.
          </p>
        )}
      </div>
    </div>
  );
}
